import { JSX } from "react";
import { BaseComponent } from "../../Core/BaseComponent/BaseComponent";
import React from "react";
import { LayoutContainer } from "../Layout/LayoutContainer";
import { Stack } from "../Stack/Stack";
import { Box } from "../Box/Box";

export class PageNav extends BaseComponent<IPageNavProps, IPageNavState>
{
    private static readonly TIMER_INTERVAL = 1000;
    private static readonly INITIAL_TIMER_DELAY = 300;
    private static readonly SECTION_DETECTION_OFFSET = 100;
    private static readonly SECTION_SCROLL_OFFSET = 50;

    private timer = 0;
    private scrollHandlerId = '';

    constructor(props: IPageNavProps)
    {
        super(props);
        this.state =
        {
            activeSectionId: '',
        };
    }

    public componentDidMount(): void
    {
        window.setTimeout(() => this.setActiveSection(), PageNav.INITIAL_TIMER_DELAY);
        this.timer = window.setInterval(() => this.setActiveSection(), PageNav.TIMER_INTERVAL);
        this.components.addLayoutContainerScrollHandler(e => this.handleLayoutContainerScroll(e), id => this.scrollHandlerId = id);
    }

    public componentWillUnmount(): void
    {
        window.clearInterval(this.timer);
        this.components.removeLayoutContainerScrollHandler(this.scrollHandlerId);
    }

    private setActiveSection(): void
    {
        const contentElement = document.getElementById(LayoutContainer.layoutContentId);
        if (!contentElement)
        {
            return;
        }

        const contentRectangle = contentElement.getBoundingClientRect();
        let activeSectionId = '';

        for (const section of this.props.sections)
        {
            const sectionElement = document.getElementById(`${section.id}`);
            if (!sectionElement)
            {
                continue;
            }

            const rectangle = sectionElement.getBoundingClientRect();
            if (rectangle.top - contentRectangle.top > PageNav.SECTION_DETECTION_OFFSET)
            {
                continue;
            }

            activeSectionId = section.id;
        }
        this.setState({ activeSectionId: activeSectionId });
    }

    private handleSectionClick(sectionId: string): void
    {
        const contentElement = document.getElementById(LayoutContainer.layoutContentId);
        const sectionElement = document.getElementById(`${sectionId}`);
        if (!contentElement || !sectionElement) return;

        const containerTop = contentElement.getBoundingClientRect().top;
        const sectionTop = sectionElement.getBoundingClientRect().top;

        contentElement.scrollTo({
            top: contentElement.scrollTop + (sectionTop - containerTop) - PageNav.SECTION_SCROLL_OFFSET,
            behavior: 'smooth',
        });
    }

    private handleLayoutContainerScroll(e: React.UIEvent<HTMLDivElement, UIEvent>): void
    {
        this.setActiveSection();
    }

    public render(): JSX.Element
    {
        return (
            <Stack
                spacing="3xl"
                style={{ padding: 'var(--spacing-sd-system-dimension-spacing-small)' }}
            >
                {this.props.sections.map(i =>
                    <Box
                        key={i.id}
                        style={{
                            backgroundColor: (this.state.activeSectionId === i.id ? 'var(--colors-sd-system-color-interaction-selected-surface)' : undefined),
                            borderRadius: 'var(--radii-sd-system-dimension-radius-medium)',
                            padding: 'var(--spacing-sd-system-dimension-spacing-extra-small)',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'clip',
                        }}
                        onClick={() => this.handleSectionClick(i.id)}
                    >
                        {i.name}
                    </Box>
                )}
            </Stack>
        );
    }

    public static update(sections: IPageNavSection[], sectionOrId: IPageNavSection | string, index: number): IPageNavSection[]
    {
        if (typeof sectionOrId === 'string')
        {
            return sections.filter(i => i.id != sectionOrId);
        }
        else
        {
            sectionOrId.index = index;
            return [...sections, sectionOrId].sort((a, b) => a.index - b.index);
        }
    }
}

export interface IPageNavProps
{
    sections: IPageNavSection[];
}

export interface IPageNavSection
{
    id: string;
    index: number;
    name: string;
}

export interface IPageNavState
{
    activeSectionId: string;
}
